/**
 * AdminTrainingDashboard Component
 *
 * Main admin page for Q.AI RLHF Training Data Management
 * Task: F.1 - Training Data Page
 *
 * Route: /admin/training (via admin panel)
 *
 * Features:
 * - Date filter with presets
 * - Export training data (JSON/CSV)
 * - Scoreboard with quality distribution
 * - Bulk score import
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Tooltip,
  Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolIcon from '@mui/icons-material/School';
import { toast } from 'react-toastify';

// Components
import DateFilterDropdown from './DateFilterDropdown';
import TrainingScoreboard from './TrainingScoreboard';
import BulkScoreUpload from './BulkScoreUpload';

// API
import {
  exportTrainingData,
  importTrainingScores,
  getTrainingStats,
  downloadJson,
  downloadBlob
} from '../../api/trainingApi';

/**
 * AdminTrainingDashboard Component
 */
const AdminTrainingDashboard = () => {
  // State
  const [datePreset, setDatePreset] = useState('last7days');
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    to: new Date()
  });
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Fetch stats on mount and date change
  const fetchStats = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const result = await getTrainingStats({
        fromDate: dateRange.from,
        toDate: dateRange.to
      });

      if (result.success) {
        setStats(result);
        setLastRefresh(new Date());
      } else {
        console.error('[AdminTrainingDashboard] Failed to fetch stats:', result.error);
        // Don't show error toast for initial load - API may not be ready
      }
    } catch (error) {
      console.error('[AdminTrainingDashboard] Error fetching stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle date filter change
  const handleDateChange = (preset, range) => {
    setDatePreset(preset);
    if (range) {
      setDateRange(range);
    }
  };

  // Handle export
  const handleExport = async (format = 'json') => {
    setIsExporting(true);
    try {
      const result = await exportTrainingData({
        preset: datePreset,
        format,
        fromDate: dateRange.from,
        toDate: dateRange.to
      });

      if (result.success) {
        if (result.isBlob) {
          downloadBlob(result.data, result.filename);
        } else {
          // For JSON, download the data
          const filename = `qai_training_${datePreset}_${new Date().toISOString().split('T')[0]}.json`;
          downloadJson(result, filename);
        }
        toast.success(`Exported ${result.totalConversations || 'training'} data successfully`);
      } else {
        toast.error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('[AdminTrainingDashboard] Export error:', error);
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle bulk import
  const handleBulkImport = async (data) => {
    setIsUploading(true);
    try {
      const result = await importTrainingScores(data);

      if (result.success) {
        toast.success(result.message || `Imported ${result.imported} scores`);
        // Refresh stats after import
        await fetchStats();
      } else {
        toast.error(result.error || 'Import failed');
      }

      return result;
    } catch (error) {
      console.error('[AdminTrainingDashboard] Import error:', error);
      toast.error('Import failed');
      return { success: false, error: error.message };
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: '#191b20',
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <SchoolIcon sx={{ color: '#7c3aed', fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              Training Data Management
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Export conversations for Claude review, import scores, and track quality metrics.
          </Typography>
        </Box>

        {/* Last refresh indicator */}
        {lastRefresh && (
          <Chip
            label={`Last updated: ${lastRefresh.toLocaleTimeString()}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.75rem'
            }}
          />
        )}
      </Box>

      {/* Filters & Actions Bar */}
      <Paper
        sx={{
          backgroundColor: '#292b2f',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 2,
          p: 2,
          mb: 3
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          {/* Date Filter */}
          <DateFilterDropdown
            value={datePreset}
            onChange={handleDateChange}
            customRange={datePreset === 'custom' ? dateRange : undefined}
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh statistics">
              <Button
                variant="outlined"
                size="small"
                onClick={fetchStats}
                disabled={isLoadingStats}
                sx={{
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.7)',
                  minWidth: 40,
                  '&:hover': { borderColor: 'rgba(255,255,255,0.3)' }
                }}
              >
                {isLoadingStats ? (
                  <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.5)' }} />
                ) : (
                  <RefreshIcon />
                )}
              </Button>
            </Tooltip>

            <Tooltip title="Export training data as JSON">
              <Button
                variant="contained"
                size="small"
                startIcon={isExporting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DownloadIcon />}
                onClick={() => handleExport('json')}
                disabled={isExporting}
                sx={{
                  backgroundColor: '#7c3aed',
                  '&:hover': { backgroundColor: '#6d28d9' }
                }}
              >
                Export JSON
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Scoreboard Section */}
      <Paper
        sx={{
          backgroundColor: '#292b2f',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 2,
          p: 3,
          mb: 3
        }}
      >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
          Quality Scoreboard
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
          Distribution of response quality scores for the selected period.
        </Typography>

        <TrainingScoreboard
          stats={stats}
          isLoading={isLoadingStats}
          showSummary={true}
        />
      </Paper>

      {/* Bulk Import Section */}
      <BulkScoreUpload
        onUpload={handleBulkImport}
        isUploading={isUploading}
        onUploadComplete={() => {
          // Stats already refreshed in handleBulkImport
        }}
      />

      {/* Help Section */}
      <Paper
        sx={{
          backgroundColor: 'rgba(124, 58, 237, 0.1)',
          border: '1px solid rgba(124, 58, 237, 0.2)',
          borderRadius: 2,
          p: 2,
          mt: 3
        }}
      >
        <Typography variant="subtitle2" sx={{ color: '#a78bfa', mb: 1 }}>
          Weekly Review Workflow
        </Typography>
        <Box component="ol" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', m: 0, pl: 2.5 }}>
          <li>Export last 7 days of conversations using the button above</li>
          <li>Review conversations with Claude and score each response (-2 to +2)</li>
          <li>Save scores as JSON following the template format</li>
          <li>Import scored JSON using the Bulk Import section</li>
          <li>Monitor the scoreboard to track quality improvements</li>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminTrainingDashboard;
